import React, { useState } from 'react';
import { Calendar, Clock, MapPin, FileText, Users, Edit, Check, X, Eye, EyeOff, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Produksjon, produksjonAPI } from '../../services/api';
import { queryKeys } from '../../lib/queryKeys';
import { Button } from '../ui/button';
import TomTomPlacesAutocomplete, { TomTomPlaceData } from '../common/TomTomPlacesAutocomplete';
import { beregnSluttDato, erSluttTidNesteDag } from '../../utils/sluttTid';
import { isoTilLokalDatetimeLocal, lokalDatetimeLocalTilIso, isoTilLokalHHMM } from '../../utils/lokalTid';

interface ProductionHeaderProps {
  produksjon: Produksjon;
  bemanningStats: {
    totalt: number;
    bekreftet: number;
    planlagt: number;
    avvist: number;
    ikkeSvart: number;
  };
  visMedarbeidere: boolean;
  visPlan: boolean;
  visOppmote: boolean;
  onToggleMedarbeidere: () => void;
  onTogglePlan: () => void;
  onToggleOppmote: () => void;
  formatDato: (tid: string) => string;
  formatTid: (tid: string) => string;
  readOnly?: boolean;
}

const ProductionHeaderTailwind: React.FC<ProductionHeaderProps> = ({
  produksjon,
  bemanningStats,
  visMedarbeidere,
  visPlan,
  visOppmote,
  onToggleMedarbeidere,
  onTogglePlan,
  onToggleOppmote,
  formatDato,
  formatTid,
  readOnly = false,
}) => {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [editedNavn, setEditedNavn] = useState(produksjon.navn);
  // Starttid vises og redigeres som lokal `datetime-local`-verdi (YYYY-MM-DDTHH:mm).
  // `produksjon.tid` er ISO 8601 UTC fra API-et - konverteres via isoTilLokalDatetimeLocal,
  // ALDRI med slice() direkte på UTC-strengen (det ville vist UTC-klokkeslettet som om det
  // var lokal tid). Se utils/lokalTid.ts.
  const [editedTidLocal, setEditedTidLocal] = useState(isoTilLokalDatetimeLocal(produksjon.tid));
  // Sluttid vises og redigeres som rent klokkeslett (HH:mm) i lokal tid, uten dato - datoen
  // avledes fra starttiden (se erSluttTidNesteDag/beregnSluttDato i utils/sluttTid.ts).
  const [editedSluttTidHHMM, setEditedSluttTidHHMM] = useState(
    produksjon.slutt_tid ? isoTilLokalHHMM(produksjon.slutt_tid) : ''
  );
  const [sluttTidTouched, setSluttTidTouched] = useState(false);
  const [editedPlassering, setEditedPlassering] = useState(produksjon.plassering || '');
  const [editedBeskrivelse, setEditedBeskrivelse] = useState(produksjon.beskrivelse || '');
  const [editedPublisert, setEditedPublisert] = useState(produksjon.publisert);
  const [saving, setSaving] = useState(false);
  const [placeData, setPlaceData] = useState<TomTomPlaceData | null>(
    produksjon.plassering_lat && produksjon.plassering_lng
      ? {
          searchText: produksjon.plassering || '',
          address: produksjon.plassering_adresse || '',
          latitude: produksjon.plassering_lat,
          longitude: produksjon.plassering_lng,
        }
      : null
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      // Extract searchText from editedPlassering if it contains " - "
      // editedPlassering might be "searchText - address" or just "searchText"
      let plasseringText = editedPlassering;
      if (placeData) {
        // If we have placeData, use the searchText from it
        plasseringText = placeData.searchText;
      } else if (editedPlassering.includes(' - ')) {
        // If editedPlassering contains " - ", extract the part before " - "
        plasseringText = editedPlassering.split(' - ')[0];
      }
      
      // tid sendes som korrekt ISO 8601 (UTC), avledet fra den lokale
      // datetime-local-verdien - samme mønster som NyProduksjonDialog.
      const nyTidISO = lokalDatetimeLocalTilIso(editedTidLocal);

      // Prepare update data - explicitly include coordinates even if null
      const updateData: any = {
        navn: editedNavn,
        tid: nyTidISO,
        plassering: plasseringText, // Søketekst (f.eks. "Sandvika kulturhus")
        beskrivelse: editedBeskrivelse,
        publisert: editedPublisert,
      };
      // sluttTid sendes bare når brukeren faktisk har rørt sluttid-feltet i denne
      // redigeringsøkten. Sendes tid uten sluttTid, flytter backend eksisterende
      // slutt_tid med samme differanse (se Docs/Produksjons_bemanning.md).
      const [datoDel, tidDel] = editedTidLocal.split('T');
      const nySluttTidISO = editedSluttTidHHMM
        ? beregnSluttDato(datoDel, tidDel, editedSluttTidHHMM).toISOString()
        : null;
      if (sluttTidTouched) {
        updateData.sluttTid = nySluttTidISO;
      }

      // Always include plassering fields - use null if placeData is not set
      if (placeData) {
        updateData.plassering_lat = placeData.latitude;
        updateData.plassering_lng = placeData.longitude;
        updateData.plassering_adresse = placeData.address;
      } else {
        // Explicitly set to null to clear coordinates if no placeData
        updateData.plassering_lat = null;
        updateData.plassering_lng = null;
        updateData.plassering_adresse = null;
      }

      const response = await produksjonAPI.updateProduksjon(produksjon.id, updateData);

      // Update local produksjon object with new values for immediate UI update
      produksjon.navn = editedNavn;
      produksjon.tid = nyTidISO;
      if (sluttTidTouched) {
        produksjon.slutt_tid = nySluttTidISO;
      } else if (response.produksjon.slutt_tid !== undefined) {
        // Ikke rørt av brukeren - backend kan ha flyttet slutt_tid når tid endret seg.
        produksjon.slutt_tid = response.produksjon.slutt_tid;
      }
      produksjon.plassering = plasseringText;
      produksjon.plassering_lat = placeData?.latitude ?? null;
      produksjon.plassering_lng = placeData?.longitude ?? null;
      produksjon.plassering_adresse = placeData?.address ?? null;
      produksjon.beskrivelse = editedBeskrivelse;
      produksjon.publisert = editedPublisert;
      
      // Invalidate React Query cache to refetch fresh data from backend
      await queryClient.invalidateQueries({ queryKey: queryKeys.productions.detail(produksjon.id) });
      
      setEditMode(false);
    } catch (error: any) {
      console.error('Kunne ikke lagre endringer:', error);
      toast.error(error?.response?.data?.error || 'Kunne ikke lagre endringer');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedNavn(produksjon.navn);
    setEditedTidLocal(isoTilLokalDatetimeLocal(produksjon.tid));
    setEditedSluttTidHHMM(produksjon.slutt_tid ? isoTilLokalHHMM(produksjon.slutt_tid) : '');
    setSluttTidTouched(false);
    setEditedPlassering(produksjon.plassering || '');
    setEditedBeskrivelse(produksjon.beskrivelse || '');
    setEditedPublisert(produksjon.publisert);
    // Reset placeData to original values
    setPlaceData(
      produksjon.plassering_lat && produksjon.plassering_lng
        ? {
            searchText: produksjon.plassering || '',
            address: produksjon.plassering_adresse || '',
            latitude: produksjon.plassering_lat,
            longitude: produksjon.plassering_lng,
          }
        : null
    );
    setEditMode(false);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
      {/* Header med tittel og edit-knapp - Mobile responsive */}
      <div className="flex justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
        {editMode ? (
          <input
            type="text"
            value={editedNavn}
            onChange={(e) => setEditedNavn(e.target.value)}
            placeholder="Navn på produksjon"
            className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 min-w-0 flex-1">{produksjon.navn}</h1>
        )}
        {!readOnly && (
          !editMode ? (
            <button
              onClick={() => {
                setSluttTidTouched(false);
                setEditMode(true);
              }}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors shrink-0"
              title="Rediger detaljer"
            >
              <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          ) : (
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                title="Lagre"
              >
                <Check className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Avbryt"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          )
        )}
      </div>

      {/* Info grid - Mobile: 1 col, Tablet: 2 col, Desktop: 3 col */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Dato og tid */}
        <div>
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span className="text-xs sm:text-sm font-medium">Dato og tid</span>
          </div>
          {editMode ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={editedTidLocal}
                  onChange={(e) => setEditedTidLocal(e.target.value)}
                  className="min-w-0 flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-400 shrink-0">–</span>
                <input
                  type="time"
                  aria-label="Slutt"
                  value={editedSluttTidHHMM}
                  onChange={(e) => {
                    setEditedSluttTidHHMM(e.target.value);
                    setSluttTidTouched(true);
                  }}
                  className="w-24 sm:w-28 shrink-0 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {erSluttTidNesteDag(editedTidLocal.slice(11, 16), editedSluttTidHHMM) && (
                <p className="text-xs text-muted-foreground">Neste dag</p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm sm:text-base font-semibold text-gray-900">{formatDato(produksjon.tid)}</p>
              <p className="text-xs sm:text-sm text-gray-600">
                kl. {formatTid(produksjon.tid)}
                {produksjon.slutt_tid ? `–${formatTid(produksjon.slutt_tid)}` : ''}
              </p>
            </div>
          )}
        </div>

        {/* Plassering */}
        <div>
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <MapPin className="h-4 w-4" />
            <span className="text-xs sm:text-sm font-medium">Plassering</span>
          </div>
          {editMode ? (
            <TomTomPlacesAutocomplete
              value={editedPlassering}
              onChange={(newValue) => {
                // When user types, update editedPlassering
                // When suggestion is selected, onPlaceSelect will be called
                setEditedPlassering(newValue);
                // If user types after selecting a suggestion, clear placeData
                // (unless the newValue still contains the address format)
                if (!newValue.includes(' - ') || newValue !== `${placeData?.searchText} - ${placeData?.address}`) {
                  // User is typing something new, clear placeData
                  if (placeData) {
                    console.log('[DEBUG] User typing - clearing placeData');
                    setPlaceData(null);
                  }
                }
              }}
              placeholder="Søk etter plassering..."
              disabled={saving}
              onPlaceSelect={(place) => {
                console.log('[DEBUG] onPlaceSelect called with:', JSON.stringify(place, null, 2));
                setPlaceData(place);
                // Update editedPlassering to show "searchText - address" in the field
                setEditedPlassering(`${place.searchText} - ${place.address}`);
              }}
            />
          ) : (
            (() => {
              const hasLat = produksjon.plassering_lat != null && !isNaN(Number(produksjon.plassering_lat));
              const hasLng = produksjon.plassering_lng != null && !isNaN(Number(produksjon.plassering_lng));
              const hasCoordinates = hasLat && hasLng;
              const displayText = produksjon.plassering && produksjon.plassering_adresse
                ? `${produksjon.plassering} - ${produksjon.plassering_adresse}`
                : produksjon.plassering || 'Ikke angitt';
              
              if (hasCoordinates) {
                return (
                  <button
                    onClick={() => {
                      const url = `https://www.google.com/maps?q=${produksjon.plassering_lat},${produksjon.plassering_lng}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    className="w-full text-left group"
                    title="Klikk for å vise i kart"
                    aria-label="Vis plassering i kart"
                  >
                    <p className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-primary transition-colors inline-flex items-center gap-2">
                      <span>{displayText}</span>
                      <Navigation className="h-4 w-4 text-primary opacity-60 group-hover:opacity-100 transition-opacity shrink-0" />
                    </p>
                  </button>
                );
              }
              
              return (
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {displayText}
                </p>
              );
            })()
          )}
        </div>

        {/* Beskrivelse - Flyttet inn i grid for konsistens */}
        <div className="sm:col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <FileText className="h-4 w-4" />
            <span className="text-xs sm:text-sm font-medium">Beskrivelse</span>
          </div>
          {editMode ? (
            <textarea
              value={editedBeskrivelse}
              onChange={(e) => setEditedBeskrivelse(e.target.value)}
              placeholder="Beskrivelse av produksjonen"
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          ) : (
            <p className="text-xs sm:text-sm text-gray-700">{produksjon.beskrivelse || 'Ingen beskrivelse'}</p>
          )}
        </div>

        {/* Publisert status */}
        <div className="sm:col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            {editedPublisert ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
            <span className="text-xs sm:text-sm font-medium">Status</span>
          </div>
          {editMode ? (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={editedPublisert}
                onChange={(e) => setEditedPublisert(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-gray-900">
                {editedPublisert ? 'Publisert' : 'Upublisert'}
              </span>
            </label>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                  produksjon.publisert
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {produksjon.publisert ? (
                  <>
                    <Eye className="h-3 w-3" />
                    Publisert
                  </>
                ) : (
                  <>
                    <EyeOff className="h-3 w-3" />
                    Upublisert
                  </>
                )}
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Knapper - Mobile: Grid 2 col, Desktop: Flex */}
      <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
        <button
          onClick={onToggleMedarbeidere}
          className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors ${
            visMedarbeidere
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
          }`}
        >
          <Users className="h-4 w-4" />
          <span className="text-xs sm:text-sm">Bemanning ({bemanningStats.totalt})</span>
        </button>
        <button
          onClick={onTogglePlan}
          className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors ${
            visPlan
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
          }`}
        >
          <Clock className="h-4 w-4" />
          <span className="text-xs sm:text-sm">Plan</span>
        </button>
        <button
          onClick={onToggleOppmote}
          className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors col-span-2 sm:col-span-1 ${
            visOppmote
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
          }`}
        >
          <Clock className="h-4 w-4" />
          <span className="text-xs sm:text-sm">Oppmøtetider</span>
        </button>
      </div>
    </div>
  );
};

export default ProductionHeaderTailwind;

